import { Component } from "@angular/core";
import { AbstractControl, FormArray, FormControl, FormGroup } from "@angular/forms";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { combineLatest } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { UnsubscribeComponent } from "../../generals/Components";
import { LabelValue, PaymentMethodItem, ShippingMethodEditor, ShippingMethodEditorPrice } from "../../interfaces/interfaces";
import { ApiService } from "../../services/api.service";

@Component({
    selector: 'app-shippingmethodeditor',
    templateUrl: './shippingmethodeditor.component.html',
    styleUrls: ['shippingmethodeditor.component.scss']
})
export class ShippingMethodEditorComponent extends UnsubscribeComponent {

    id: string = null;
    loading: boolean = true;
    paymentMethods: LabelValue[] = [];
    formGroup: FormGroup = new FormGroup({
        info: new FormControl(null),
        enabled: new FormControl(null),
        vatRate: new FormControl(null),
        prices: new FormArray([]),
        pricesTouched: new FormControl(false),
        allowedPaymentMethods: new FormControl([]),
        isNet: new FormControl(true)
    });

    constructor(
        private config: DynamicDialogConfig,
        private ref: DynamicDialogRef,
        private apiService: ApiService
    ) {
        super();
        this.id = this.config.data.id;
    }

    ngOnInit(): void {
        this.load();
    }

    load(): void {
        if (this.id !== null) {
            combineLatest([
                this.apiService.getShippingMethod(this.id),
                this.apiService.getSimpleTable('paymentmethods')
            ]).pipe(takeUntil(this.unsubscribe)).subscribe(([{shippingmethodEditor}, {data}]) => {
                this.paymentMethods = (data as PaymentMethodItem[]).map((paymentMethodItem: PaymentMethodItem) => ({
                    label: paymentMethodItem.title,
                    value: paymentMethodItem.id
                }));
                this.formGroup.patchValue(shippingmethodEditor);
                this.addPriceFormGroups(shippingmethodEditor.prices);
                this.appendEvents();
            }).add(() => {
                this.loading = false;
            });
        } else {
            this.apiService.getSimpleTable('paymentmethods').pipe(takeUntil(this.unsubscribe)).subscribe(({data}) => {
                this.paymentMethods = (data as PaymentMethodItem[]).map((paymentMethodItem: PaymentMethodItem) => ({
                    label: paymentMethodItem.title,
                    value: paymentMethodItem.id
                }));
            }).add(() => {
                this.loading = false;
            });
        }
    }

    appendEvents(): void {
        this.formGroup.get('vatRate').valueChanges.subscribe((vatRate: number) => {
            const isNet: boolean = this.formGroup.get('isNet').value;
            this.getPricesFormGroups().forEach((formGroup: FormGroup) => {
                if (isNet) {
                    const netPrice: number = formGroup.get('netPrice').value;
                    formGroup.get('grossPrice').setValue(netPrice / 100 * (100 + vatRate), {
                        emitEvent: false
                    });
                } else {
                    const grossPrice: number = formGroup.get('grossPrice').value;
                    formGroup.get('netPrice').setValue(grossPrice / (100 + vatRate) * 100, {
                        emitEvent: false
                    });
                }
            });
        });
    }

    addPriceFormGroups(shippingMethodEditorPrices: ShippingMethodEditorPrice[]): void {
        shippingMethodEditorPrices.forEach((shippingMethodEditorPrice: ShippingMethodEditorPrice) => {
            this.addPriceFormGroup(shippingMethodEditorPrice);
        });
    }

    addPriceFormGroup(shippingMethodEditorPrice: ShippingMethodEditorPrice = null): void {
        const shippingMethodEditorPriceFormGroup: FormGroup = new FormGroup({
            min: new FormControl(shippingMethodEditorPrice !== null ? shippingMethodEditorPrice.min : 0),
            netPrice: new FormControl(shippingMethodEditorPrice !== null ? shippingMethodEditorPrice.netPrice : 0),
            grossPrice: new FormControl(shippingMethodEditorPrice !== null ? shippingMethodEditorPrice.grossPrice : 0)
        });
        shippingMethodEditorPriceFormGroup.get('netPrice').valueChanges.subscribe((netPrice: number) => {
            const vatRate: number = this.formGroup.get('vatRate').value;
            shippingMethodEditorPriceFormGroup.get('grossPrice').setValue(netPrice / 100 * (100 + vatRate), {
                emitEvent: false
            });
        });
        shippingMethodEditorPriceFormGroup.get('grossPrice').valueChanges.subscribe((grossPrice: number) => {
            const vatRate: number = this.formGroup.get('vatRate').value;
            shippingMethodEditorPriceFormGroup.get('netPrice').setValue(grossPrice / (100 + vatRate) * 100, {
                emitEvent: false
            });
        });
        (this.formGroup.get('prices') as FormArray).push(shippingMethodEditorPriceFormGroup);
    }

    removePriceFormGroup(index: number): void {
        if (confirm('Biztosan töröljük ezt a szállítási ársávot?')) {
            (this.formGroup.get('prices') as FormArray).removeAt(index);
        }
    }

    getPricesFormGroups(): AbstractControl[] {
        return (this.formGroup.get('prices') as FormArray).controls;
    }

    save(): void {
        const shippingMethodEditor: ShippingMethodEditor = this.formGroup.getRawValue();
        shippingMethodEditor.pricesTouched = this.getPricesFormGroups().some((formGroup: FormGroup) => formGroup.touched);
        this.apiService.updateShippingMethod(this.id, shippingMethodEditor).pipe(takeUntil(this.unsubscribe)).subscribe(() => {
            this.close(true);
        });
    }

    close(result: boolean = false): void {
        this.ref.close(result);
    }

}
